import { UtilityService } from '../types/utility.types';

/**
 * F-200 (Audit 19): single canonical implementation of sub-broker dashboard
 * stats — previously duplicated across SubBrokerDashboard/index.tsx,
 * SubBrokerDashboardView.tsx and SubBrokerOverview.tsx, where the three copies
 * had already drifted (the 'Pending' status check never matched canonical
 * pre-live statuses, so the Pending KPI was permanently 0).
 *
 * Status vocabulary is the Audit 09–11 canonical title-case lifecycle.
 */

export const SUB_BROKER_LIVE_STATUS = 'Live';

/** Canonical pre-live statuses (a contract on its way to Live). */
export const SUB_BROKER_PENDING_STATUSES: readonly string[] = [
  'Signed - Pending',
  'Locked in / Awaiting Live',
  'Renewed Awaiting Live'
];

/** Statuses eligible for the upcoming-renewals list: live + pre-live work. */
export const RENEWAL_ELIGIBLE_STATUSES: readonly string[] = [
  SUB_BROKER_LIVE_STATUS,
  ...SUB_BROKER_PENDING_STATUSES
];

export const RENEWAL_WINDOW_DAYS = 90;

export interface SubBrokerStats {
  totalContracts: number;
  totalCommissionValue: number;
  totalCommissionReceived: number;
  outstandingCommission: number;
  liveContracts: number;
  pendingContracts: number;
}

/** Europe/London calendar day number (days since epoch of the London date). */
const londonDayNumber = (d: Date): number => {
  const key = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London' }).format(d);
  return Math.floor(Date.parse(`${key}T00:00:00Z`) / 86400000);
};

/**
 * Per-contract money fields. Prefer the server-computed virtuals
 * (subBrokerEntitlement / subBrokerOutstanding — Audit 03 invariants);
 * fall back to the formula only for legacy contracts lacking the snapshot.
 */
export const contractMoney = (contract: UtilityService) => {
  const contractValue = Number(contract.contractValue) || 0;
  const commissionRate = contract.subBroker?.commissionRate || 0;
  const commission = Number(contract.subBrokerEntitlement) || (contractValue * (commissionRate / 100));
  const received = Number(contract.totalCommissionReceived) || 0;
  const outstanding = contract.subBrokerOutstanding !== undefined
    ? Number(contract.subBrokerOutstanding)
    : (commission - received);
  return { contractValue, commissionRate, commission, received, outstanding };
};

export const computeSubBrokerStats = (contracts: UtilityService[]): SubBrokerStats => {
  const stats: SubBrokerStats = {
    totalContracts: contracts.length,
    totalCommissionValue: 0,
    totalCommissionReceived: 0,
    outstandingCommission: 0,
    liveContracts: 0,
    pendingContracts: 0
  };

  contracts.forEach((contract) => {
    const money = contractMoney(contract);
    stats.totalCommissionValue += money.commission;
    stats.totalCommissionReceived += money.received;
    stats.outstandingCommission += money.outstanding;

    if (contract.status === SUB_BROKER_LIVE_STATUS) {
      stats.liveContracts++;
    } else if (SUB_BROKER_PENDING_STATUSES.includes(contract.status || '')) {
      stats.pendingContracts++;
    }
  });

  return stats;
};

/**
 * Contracts due for renewal: eligible statuses with a business end date within
 * the next `windowDays` London business days. Terminal/superseded contracts
 * never appear — a Cancelled contract with a future end date is not a renewal.
 */
export const upcomingRenewals = (
  contracts: UtilityService[],
  windowDays: number = RENEWAL_WINDOW_DAYS
): UtilityService[] => {
  const today = londonDayNumber(new Date());
  const limit = today + windowDays;

  return contracts
    .filter((contract) => {
      if (!contract.contractEndDate) return false;
      if (!RENEWAL_ELIGIBLE_STATUSES.includes(contract.status || '')) return false;
      const day = londonDayNumber(new Date(contract.contractEndDate));
      return day >= today && day <= limit;
    })
    .sort(
      (a, b) =>
        new Date(a.contractEndDate!).getTime() - new Date(b.contractEndDate!).getTime()
    );
};

/** Days until the business end date (London business days). */
export const londonDaysUntil = (endDate: Date | string): number =>
  londonDayNumber(new Date(endDate)) - londonDayNumber(new Date());

/** Status chip colour for the canonical lifecycle. */
export const contractStatusColor = (
  status?: string
): 'success' | 'warning' | 'error' | 'default' => {
  if (status === SUB_BROKER_LIVE_STATUS) return 'success';
  if (SUB_BROKER_PENDING_STATUSES.includes(status || '')) return 'warning';
  if (status === 'EXPIRED' || status === 'Cancelled' || status === 'Lost' || status === 'Terminated Prior to end') {
    return 'error';
  }
  return 'default';
};
